import { encode } from '@capsicum/common';
import { Text, TextLineOrSkeleton } from '@capsicum/common-web';
import { useNCWBalanceInfo } from '@capsicum/features/nc-wallet/shared';
import styled from 'styled-components';

export type NCWBalanceInfoProps = {
	/** Alternate currency amount. e.g. 50 EUR */
	altAmount?: string;

	/** Default currency amount or any amount. e.g. 5 ACM, 5 CHZ */
	balanceAmount: string;

	/** Defines if token has decimals or not */
	hasDecimals?: boolean;

	isLoading: boolean;
};

/**
 * Renders balance info like in the following examples:
 * Balance: 5 ACM ≈ 5.55 EUR
 * Balance: 5 ACM
 * @component
 * @param {string} balanceAmount The main balance to display (e.g., "5 ACM", "5 CHZ")
 * @param {string} altAmount Optional alternate currency value (e.g., "50 EUR")
 * @param {boolean} hasDecimals Whether the token has decimals
 * @param {boolean} isLoading Whether to display loading placeholders
 * @returns {JSX.Element} Rendered balance information
 */
export const NCWBalanceInfo = ({ altAmount, balanceAmount, hasDecimals, isLoading }: NCWBalanceInfoProps) => {
	const { intlText } = useNCWBalanceInfo();

	return (
		<S.Info>
			<S.Balance variant="text3">{intlText.balance}</S.Balance>
			<Text variant="text1">{balanceAmount}</Text>
			{!hasDecimals && (
				<>
					{!!altAmount && <S.Balance variant="text3">{encode('doubleTilde')}</S.Balance>}
					{!!altAmount && (
						<TextLineOrSkeleton isLoading={isLoading} lineWidth={60}>
							<S.Balance variant="text3">{altAmount}</S.Balance>
						</TextLineOrSkeleton>
					)}
				</>
			)}
		</S.Info>
	);
};

namespace S {
	export const Info = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const Balance = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
